// @ts-nocheck
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * An event is registerable only when it has at least one active row in
 * event_division_settings with has_boys or has_girls true.
 * (event_divisions is a legacy/AI-builder table and is counted as a fallback
 * so older events that only have those rows aren't wrongly flagged.)
 *
 * CRITICAL: this guard must distinguish "no divisions" from "couldn't read
 * divisions". A permissions/RLS gap returns zero rows with no error for some
 * roles; presenting that to a paying customer as "registration is closed" is
 * far worse than letting the page load. We only report a confirmed empty
 * state when every read succeeded AND there's no contradicting signal
 * (published event with age groups configured).
 */
export function useEventDivisionGuard(eventId?: string | null) {
  const query = useQuery({
    queryKey: ["event-division-guard", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const [settings, legacy, ageGroups, ev] = await Promise.all([
        (supabase as any)
          .from("event_division_settings")
          .select("catalog_division_id, has_boys, has_girls")
          .eq("event_id", eventId)
          .eq("is_active", true),
        (supabase as any)
          .from("event_divisions")
          .select("id")
          .eq("event_id", eventId)
          .eq("is_active", true),
        (supabase as any)
          .from("event_age_groups")
          .select("id")
          .eq("event_id", eventId),
        (supabase as any)
          .from("events")
          .select("id, status")
          .eq("id", eventId)
          .maybeSingle(),
      ]);

      const readError =
        settings.error || legacy.error
          ? settings.error?.message || legacy.error?.message
          : null;

      const settingsRows = (settings.data ?? []).filter(
        (d: any) => d.has_boys || d.has_girls
      );

      return {
        settingsCount: settingsRows.length,
        legacyCount: (legacy.data ?? []).length,
        ageGroupCount: (ageGroups.data ?? []).length,
        status: ev.data?.status ?? null,
        readError,
      };
    },
  });

  const settingsCount = query.data?.settingsCount ?? 0;
  const legacyCount = query.data?.legacyCount ?? 0;
  const ageGroupCount = query.data?.ageGroupCount ?? 0;
  const status = query.data?.status ?? null;
  const readError = query.data?.readError ?? (query.error as any)?.message ?? null;

  const hasDivisions = settingsCount > 0 || legacyCount > 0;

  // Zero rows on a published event that has age groups configured is almost
  // certainly a read failure, not a genuinely empty event.
  const suspectRead =
    !hasDivisions && status === "published" && ageGroupCount > 0;

  const unreadable = !!readError || suspectRead;

  if (eventId && !query.isLoading && unreadable) {
    console.error("[useEventDivisionGuard] divisions unreadable — failing open", {
      eventId,
      readError,
      settingsCount,
      legacyCount,
      ageGroupCount,
      status,
    });
  }

  return {
    loading: query.isLoading,
    hasDivisions,
    /** True only when every read succeeded and genuinely returned nothing. */
    confirmedNoDivisions: !query.isLoading && !hasDivisions && !unreadable,
    /** True when the read errored or looks like it was silently filtered. */
    unreadable,
    readError,
    settingsCount,
    legacyCount,
    ageGroupCount,
    refetch: query.refetch,
  };
}

/** Contact line for a public "registration isn't open" notice. */
export function useOrganiserContact(organizationId?: string | null) {
  const { data } = useQuery({
    queryKey: ["organiser-contact", organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const [settings, org] = await Promise.all([
        (supabase as any)
          .from("org_settings")
          .select("reply_to_email, display_name")
          .eq("organization_id", organizationId)
          .maybeSingle(),
        (supabase as any)
          .from("organizations")
          .select("name, website")
          .eq("id", organizationId)
          .maybeSingle(),
      ]);
      return {
        email: settings.data?.reply_to_email ?? "info@recruitlook.com",
        name: settings.data?.display_name ?? org.data?.name ?? "the organiser",
        website: org.data?.website ?? null,
      };
    },
  });

  return (
    data ?? {
      email: "info@recruitlook.com",
      name: "the organiser",
      website: null,
    }
  );
}
